import { UpdateMediaFoldersRequestParams, UpdateMediaFoldersResponseParams } from "../types";
import { useEffect, useRef, useState } from "react";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts";
import { Button, Modal, ModalActions, ModalContent, ModalTitle, Typography } from "@kaltura/ds-react-components";
import { baseUrl, postKmsData, translate, translatePlural } from "@kaltura/mediaspace-shared-utils";
import { ToastMessages } from "@kaltura/mediaspace-shared-ui";
import { ButtonClickAnalyticsType, SharedMenuItemProps } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";

export interface RemoveFromCurrentFolderModalProps extends SharedMenuItemProps {
    /** boolean flag to indicate the modal is currently open */
    isOpen: boolean;
    /** callback to execute when clicking cancel */
    onClose: () => void;
    /** entry ids to remove from currently selected folder */
    entryIds: string[];
    /** currently selected folder id in the search filters to remove entries form */
    currentFolderId: string;
    /**
     * callback for letting the app know media list should be refreshed
     */
    reloadMedia?: () => void;
}

const baseClassName = "kms-ds-folders-remove-from-current-folder-modal";

/**
 * remove from current folder modal
 * handles removing media from the currently filtered folder
 * displays confirmation and handles the delete action
 * used in:
 * my-media single entry action, my-media bulk action
 */
export function RemoveFromCurrentFolderModal({
    isOpen,
    onClose,
    entryIds,
    onCloseMenu,
    currentFolderId,
    reloadMedia,
}: RemoveFromCurrentFolderModalProps) {
    const [isProcessing, setIsProcessing] = useState(false);
    const wasSubmitted = useRef(false);

    /**
     * reload my media list after update media folders request was submitted
     * done here instead of in below handleRemoveFromCurrentFolder to
     * allow media actions menu to close and only then reload the media list
     */
    useEffect(() => {
        return () => {
            if (wasSubmitted.current) {
                reloadMedia?.();
            }
        };
    }, []);

    const { showToast } = useToastsContext();
    const sendButtonAnalytics = useButtonAnalytics();

    const handleRemoveFromCurrentFolder = async () => {
        sendButtonAnalytics(
            "Remove from folder - Remove",
            ButtonClickAnalyticsType.DELETE,
            entryIds.length === 1 ? entryIds[0] : undefined
        );
        setIsProcessing(true);
        const requestData: UpdateMediaFoldersRequestParams = {
            entryIds: entryIds,
            addFolderIds: [],
            removeFolderIds: [currentFolderId],
        };
        try {
            const result: UpdateMediaFoldersResponseParams = await postKmsData(
                `${baseUrl}/folders/entry/update`,
                requestData
            );
            setIsProcessing(false);

            if (result.successMessages && result.successMessages.length > 0) {
                // max one success message is returned (remove from folder only)
                showToast({
                    severity: "success",
                    message: result.successMessages[0],
                });
                wasSubmitted.current = true;
                onCloseMenu?.();
            }
            // the server returns either successMessages or errorMessages - not both
            else {
                if (result.errorMessages && result.errorMessages.length > 0) {
                    showToast({
                        severity: "error",
                        message: (
                            <ToastMessages
                                title={translate("Failed to remove the following media:")}
                                messages={result.errorMessages}
                            />
                        ),
                    });
                }
            }
        }
        catch (e) {
            setIsProcessing(false);
            showToast({
                severity: "error",
                message: translate("Failed to remove media from current folder"),
            });
        }
    };

    const handleClose = () => {
        sendButtonAnalytics(
            "Remove from folder - Cancel",
            ButtonClickAnalyticsType.CLOSE,
            entryIds.length === 1 ? entryIds[0] : undefined
        );
        onClose();
    };

    return (
        <Modal
            classes={{
                root: `${baseClassName}-root`,
                paper: `${baseClassName}-paper`,
            }}
            open={isOpen}
            fullWidth={true}
            onKeyDown={(ev) => ev.stopPropagation()}
        >
            <ModalTitle className={`${baseClassName}-title`}>{translate("Remove from folder?")}</ModalTitle>
            <ModalContent className={`${baseClassName}-content`}>
                <Typography variant={"body1"} paragraph={true} align={"center"}>
                    {translatePlural(
                        "This item will be removed from the folder, but not deleted.",
                        "These items will be removed from the folder, but not deleted.",
                        [entryIds.length]
                    )}
                    <br />
                    {translate("This action cannot be undone.")}
                </Typography>
            </ModalContent>
            <ModalActions className={`${baseClassName}-actions`}>
                <Button
                    onClick={handleClose}
                    variant={"borderless"}
                    color={"secondary"}
                    className={`${baseClassName}-actions-cancel`}
                >
                    {translate("Cancel")}
                </Button>
                <Button
                    loading={isProcessing}
                    disabled={isProcessing}
                    className={`${baseClassName}-actions-submit`}
                    color={"danger"}
                    onClick={handleRemoveFromCurrentFolder}
                >
                    {translate("Remove")}
                </Button>
            </ModalActions>
        </Modal>
    );
}

export default RemoveFromCurrentFolderModal;
